export enum ClaimJourneyEntryPoint {
  AccountNewClaim = 'account-new-claim',
  AccountDraft = 'account-draft',
  ClaimLandingDraft = 'claim-landing-draft',
  ClaimLandingLoginless = 'claim-landing-loginless',
  ClaimLandingLoggedIn = 'claim-landing-logged-in',
  DirectLinkLoginless = 'direct-link-loginless',
  DirectLinkLoggedIn = 'direct-link-logged-in',
  XtranetManualClaim = 'xtranet-manual-claim',
}

export interface IBWClaimsPortalMetrics {
  claimReference?: string;
  bookingID?: string;
  claimTypeName?: string;
  policyName?: string;
  partnerName?: string;
  partnerID?: string;
  subsidiaryName?: string;
  subsidiaryID?: string;
  policyTypeGroupName?: string;
}

export interface IBWClaimsJourneyTrackingArgs extends IBWClaimsPortalMetrics {
  claimJourneyID?: string | null;
  claimSelectorName?: string;
  decisionPointsName?: string;
  decisionPointsAnswer?: string;
  sectionName?: string;
  hasLeavedClaimJourney?: boolean;
  completed?: boolean;
  submitted?: boolean;
  xclaimValidationError?: unknown;
  fieldValidationErrorCount?: number;
  hasClickedHelpCenter?: boolean;
  hasClickedClaimSubmittedAlertLink?: boolean;
  hasClickedClaimSubmittedCopyURL?: boolean;
  hasClickedClaimSubmittedViewClaimButton?: boolean;
  documentName?: string;
  helpArticleSlug?: string;
  /** Unique per file picker interaction — ties together all files selected in a single upload batch. */
  uploadBatchID?: string;
  fileUploadErrors?: {
    code?: string;
    size?: number;
    type?: string;
  };
  entryPoint?: ClaimJourneyEntryPoint;
  variant?: string | null;
  isFirstSection?: boolean;
}

export interface IBWFNOLSectionTimeTrackingArgs extends IBWClaimsPortalMetrics {
  claimJourneyID?: string | null;
  timeSpent: number;
  claimTypeID?: string;
  entryPoint?: ClaimJourneyEntryPoint;
  sectionName?: string;
  documentName?: string;
  variant?: string | null;
  isFirstSection?: boolean;
}

export interface IBWFNOLSubmittedPageTimeTrackingArgs extends IBWClaimsPortalMetrics {
  timeSpent: number;
  variant?: string | null;
}

export interface IBWStoredClaimSelectorDataArgs {
  policyName: string;
  claimSelectorName: string;
  decisionPointsName: string;
  decisionPointsAnswer: string;
}

export interface IBWClaimsJourneyError {
  sectionName: string | null;
  fieldName: string | null;
  errorMessage: string;
}

export interface IBWClaimDetailsCommunicationsTrackingArgs extends IBWClaimsPortalMetrics {
  link: string;
  status?: string;
  dateSent?: string;
  linkText?: string;
  emailTemplateId?: string;
  emailTemplateSlug?: string;
}

export interface IBWRentalCoverBannerImpressionTrackingArgs {
  domID: string;
  CountryOfTravelCode: string;
  CustomerCountryCode: string;
  FromDate: string;
  ToDate: string;
  PolicyType: string;
  source?: string;
}

export interface IBWRentalCoverBannerClickTrackingArgs {
  bannerID: string;
  domID: string;
  CountryOfTravelCode: string;
  CustomerCountryCode: string;
  FromDate: string;
  ToDate: string;
  PolicyType: string;
  source?: string;
}

export interface IBwAtpQuoteSuccessArgs {
  policyholderCountry: string;
  partnerID: string;
  currency?: string;
  totalPrice?: number;
}

export interface IBwAtpEventArgs {
  policyholderCountry: string;
  partnerID: string;
  currency?: string;
  totalPrice?: number | string;
  bookingId?: string;
  previousBookingId?: string;
  autoRenew?: boolean;
}

export interface IBwAtpErrorEventArgs extends IBwAtpEventArgs {
  errorMessage?: string;
}

export interface IBwAtpPurchaseEventArgs extends IBwAtpEventArgs {
  bookingReference: string;
}

export interface IBwAtpModalActionEventArgs {
  partnerName?: string;
  bookingID: string;
  action: 'renew' | 'cancel' | 'later';
}

export interface IBWCallUsClickTrackingArgs {
  partnerSlug: string;
}

export interface IBWPayoutReceiptClickTrackingArgs extends IBWClaimsPortalMetrics {
  claimID: string;
  payoutID: string;
  receiptType: string;
  isPaymentComplete: boolean;
}

export interface IBWCESScoreTrackingArgs extends IBWClaimsPortalMetrics {
  source: string;
  score?: string;
  articleSlug?: string;
  feedback?: string;
  reviewed?: boolean;
}

export interface IBWContactModalTrackingArgs extends IBWClaimsPortalMetrics {
  contactJourneyID: string | null;
  bookingReference?: string;
  contactPurpose?: string;
  pageSlug?: string;
  articleTitle?: string;
  hasOpenedContactModal?: boolean;
  hasClosedContactModal?: boolean;
  hasClickedReturnPreviousStep?: boolean;
  hasClickedHaveMoreQuestionsButton?: boolean;
  hasClickedViewAllArticlesButton?: boolean;
  hasClickedSubmitEnquiryButton?: boolean;
  hasSubmitEnquirySuccessfully?: boolean;
}

export interface IBWQuickActionsTrackingArgs extends IBWClaimsPortalMetrics {
  quickActionLocation?: string;
  quickActionKey?: string;
}

export enum SelfServiceFlowEntryPoints {
  BookingCard = 'booking-card',
  QuickActions = 'quick-actions',
  ClaimsLanding = 'claims-landing',
  StandaloneCancellation = 'standalone-cancellation',
  Email = 'email',
}

export enum SelfServiceLoginOrigin {
  SignUpSMS = 'sms',
  SignUpEmail = 'email',
}

/**
 * Per-event OCR analytics record fired immediately on each transition.
 */
export interface IBWOCREventArgs extends IBWClaimsPortalMetrics {
  claimJourneyID?: string | null;
  /** Unique per claim attempt — ties together all OCR events within one claim draft. */
  ocr_session_id?: string;
  /** Unique per file picker interaction — ties together all files selected in a single upload batch. */
  upload_batch_id?: string;
  document_name?: string;
  document_type_name?: string;
  claimTypeID?: string;
  variant?: string;
  /** The specific event being recorded */
  event_type:
    | 'scan_started'
    | 'scan_completed'
    | 'scan_batch_completed'
    | 'scan_timeout'
    | 'cache_hit'
    | 'correction_file_added'
    | 'bypassed'
    | 'file_deleted'
    | 'help_link_clicked'
    | 'upload_error'
    | 'poll_error';
  file_name?: string;
  task_id?: string;
  ocr_status?: string;
  checks_total?: number;
  checks_passed?: number;
  checks_failed?: number;
  check_results?: string;
  scan_duration_ms?: number;
  correction_attempts?: number;
  files_in_category?: number;
  files_remaining?: number;
  /** Comma-separated keys of failed checks (e.g. "issue_date,has_signature"). Used in 'bypassed' events. */
  checks_failed_keys?: string;
  /** Where the help link was clicked: 'modal' or 'inline'. Used in 'help_link_clicked' events. */
  display_context?: string;
  /** Error message from upload or poll failure. Used in 'upload_error' and 'poll_error' events. */
  error_message?: string;
  /** HTTP status code from the failed request. Used in 'upload_error' events. */
  http_status?: number;
  /** Number of consecutive poll errors before dismissal. Used in 'poll_error' events. */
  error_count?: number;
  /** ISO 8601 UTC timestamp from the user's browser when the event was fired. */
  user_utc_timestamp?: string;
  /** Quote ID associated with the booking. */
  quote_id?: string;
  /** The document type we expect from ocrConfig (e.g. 'medical-certificate'). */
  expected_document_type?: string;
  /** The document type the OCR backend actually detected in the uploaded file. null when detection failed. */
  detected_document_type?: string | null;
  /** Whether this event was fired during orphan scan recovery at form submission time. */
  is_orphan_recovery?: boolean;
  /** Whether this scan_started event was fired from a resumed polling key (e.g. after page navigation), not a fresh upload. */
  is_resumed?: boolean;
  /** Doc Predictor: JSON array of per-file outcomes on 'scan_batch_completed' — [{file_name, task_id?, status, refund_status_confirmed?, scan_duration_ms?}]. */
  file_results?: string;
  /** Doc Predictor: true when ANY file in the batch confirmed the refund status (drives the auto-answer). Only on 'scan_batch_completed'. */
  any_refund_status_confirmed?: boolean;
  /** Experiment identifier (e.g. 'doc_predictor') — disambiguates events sharing variant values across experiments. */
  experiment_name?: string;
  /** Policy start date from the booking quote — for debugging date-range checks. */
  policy_start_date?: string;
  /** Policy end date from the booking quote — for debugging date-range checks. */
  policy_end_date?: string;
  /** Number of tokens in the OCR-extracted patient name (0 if not found). */
  extracted_name_token_count?: number;
  /** Number of insured names from the policy used for matching. */
  expected_name_count?: number;
  /** Best Jaro-Winkler similarity score across all insured names (0-1, rounded to 2dp). */
  name_match_best_score?: number;
  /** The raw issue date extracted from the document (for debugging dateInRange checks). */
  extracted_issue_date?: string;
  /** Whether the name check was auto-passed because the claim is for a non-policy person (e.g. close relative not on the policy). */
  is_non_policy_person_claim?: boolean;
  /** Dynamic per-check result fields: check_<key> → "passed" | "failed:not_found" | "failed:no_match". */
  [checkField: `check_${string}`]: string | undefined;
}

export interface IBWFileUploadEventArgs extends IBWClaimsPortalMetrics {
  claimJourneyID?: string | null;
  upload_batch_id?: string;
  document_name?: string;
  event_type:
    | 'password_protected_detected'
    | 'password_protected_file_removed'
    | 'doc_doctor_check_completed'
    | 'doc_doctor_check_timeout'
    | 'doc_doctor_file_removed'
    | 'doc_doctor_cache_hit'
    | 'duplicate_file_detected'
    | 'duplicate_file_removed';
  file_name?: string;
  file_size?: number;
  file_type?: string;
  duplicate_of_section?: string;
  user_utc_timestamp?: string;
  pass?: boolean;
  document_type?: string;
  issues?: string;
  duration_ms?: number;
}

export interface IBWClaimStartedEventArgs extends IBWClaimsPortalMetrics {
  claimJourneyID?: string | null;
  quote_id?: string;
  user_utc_timestamp?: string;
  ocr_variant?: string;
  med_cert_variant?: string;
  entryPoint?: ClaimJourneyEntryPoint;
  claimFlowAgentAssisted?: boolean;
}

export interface IBWClaimSubmittedEventArgs extends IBWClaimsPortalMetrics {
  claimJourneyID?: string | null;
  ocr_session_id?: string;
  claim_id?: string;
  claim_reference?: string;
  claim_type_id?: string;
  quote_id?: string;
  user_utc_timestamp?: string;
  ocr_variant?: string;
  med_cert_variant?: string;
  doc_doctor_files_total?: number;
  doc_doctor_files_passed?: number;
  doc_doctor_files_warned?: number;
  doc_doctor_issues?: string;
  duplicate_files_count?: number;
  duplicate_files_sections?: string;
}

export interface IBWSelfServiceTrackingArgs extends IBWClaimsPortalMetrics {
  loginFlowEntryPoint?: SelfServiceLoginOrigin;
  loginFlowCompleted?: boolean;
  claimFlowClaimsLandingCheckpoint?: boolean;
  claimFlowBookingCardCheckpoint?: boolean;
  claimFlowQuickActionsCheckpoint?: boolean;
  claimFlowLoginless?: boolean;
  claimFlowAgentAssisted?: boolean;
  claimFlowClaimSelectorCheckpoint?: boolean;
  claimFlowDraftResumedCheckpoint?: boolean;
  claimFlowCompleted?: boolean;
  entryPoint?: ClaimJourneyEntryPoint;
  modifyFlowEntryPoint?: SelfServiceFlowEntryPoints;
  modifyFlowButtonCheckpoint?: boolean;
  modifyFlowCompleted?: boolean;
  cancelFlowEntryPoint?: SelfServiceFlowEntryPoints;
  cancelFlowButtonCheckpoint?: boolean;
  cancelFlowCompleted?: boolean;
  isEligibleForCancellation?: boolean;
  isEligibleForCancellationOnXcover?: boolean;
  payoutFlowEntryPoint?: SelfServiceFlowEntryPoints;
  payoutFlowGetPaidButtonCheckpoint?: boolean;
  payoutFlowCompleted?: boolean;
}
